import { P2GButton, P2GDialog, P2GGrid, P2GLink, P2GText } from '@parcel2go/core-ui';
import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { basePriceHasIncreasedForSameNumberParcelsAndIsVolumetric, quoteSurcharges } from '~/lib/quotes';
import { BasketQuote } from '~/lib/types/basket-types';
import { toDisplayCurrency } from '~/lib/prices';
import { onlyUniqueNonBlank, SumNumber } from '~/lib/utils/array';
import { useAnalytics, useTaxName, useTenant } from '@parcel2go/npm-p2g-app';
import { SurchargeType, ToSurchargeType } from '~/lib/utils/breakdown';
import { TranslationNamespaces } from '~/lib/constants';
const BasketServiceSwitcher = lazy(() => import('~/components/core/switcher/basket-service-switcher'));

type SurchargeMessage = {
  type: SurchargeType;
  key: string;
  subKey?: string;
  messageText?: string;
  subMessageText?: string;
  amount?: string;
};

type PriceChangeDialogProps = {
  quote: BasketQuote;
  originalQuote: BasketQuote;
  onAccept: () => void;
};

export default function PriceChangeDialog({ quote, originalQuote, onAccept }: PriceChangeDialogProps) {
  const { t } = useTranslation(TranslationNamespaces.CHECKOUT);
  const taxName = useTaxName();
  const [openSwitcher, setOpenSwitcher] = useState(false);
  const { tenant } = useTenant();
  const { track } = useAnalytics();

  const messages = useMemo<SurchargeMessage[]>(() => {
    // Surcharge based messages
    const chargeableSurchargeTypes = quoteSurcharges(quote)
      .map((x) => ToSurchargeType(x.metadata?.surcharge))
      .filter(onlyUniqueNonBlank);

    // Price has increased, but the number of parcels has not changed
    if (basePriceHasIncreasedForSameNumberParcelsAndIsVolumetric(quote, originalQuote)) {
      chargeableSurchargeTypes.push('VWC');
    }

    return chargeableSurchargeTypes
      .map((surchargeType) => {
        const amountForType = toDisplayCurrency(
          quote.currency,
          SumNumber(
            quote.price.breakdown
              .filter((x) => ToSurchargeType(x.metadata?.surcharge) === surchargeType)
              .map((x) => x.amount)
          )
        );

        switch (surchargeType) {
          case 'VWC':
            return {
              type: surchargeType,
              key: 'PRICE_CHANGE_MODAL_VWC_MESSAGE',
              subKey: tenant.startsWith('PARCEL2GO') ? 'PRICE_CHANGE_MODAL_VWC_SUB_MESSAGE' : undefined,
              messageText:
                'Due to the way {{courier}} calculates the volumetric weight of the parcel, the price for this service has increased.',
              subMessageText: `<0>Click here</0> if you'd like to check your parcel's volumetric weight.`
            } as SurchargeMessage;

          case 'OOG':
            return {
              type: surchargeType,
              key: 'PRICE_CHANGE_MODAL_OOG_MESSAGE',
              subKey: 'PRICE_CHANGE_MODAL_OOG_SUB_MESSAGE',
              messageText:
                'Due to the weight and/or dimensions you have entered, {{courier}} apply an additional charge<0>*</0> for this service.',
              subMessageText:
                '<0>*</0> This charge is applied because {{courier}} would need to handle this item with more care.',
              amount: amountForType
            } as SurchargeMessage;

          case 'OOA':
            return {
              type: surchargeType,
              key: 'PRICE_CHANGE_MODAL_OOA_MESSAGE',
              subKey: 'PRICE_CHANGE_MODAL_OOA_SUB_MESSAGE',
              messageText:
                'Due to the location<0>**</0> entered, {{courier}} apply an additional charge for this service.',
              subMessageText: '<0>**</0> This charge is applied because it costs more to deliver to this location.',
              amount: amountForType
            } as SurchargeMessage;

          default:
            return null;
        }
      })
      .filter((x) => x !== null)
      .filter((x) => x.type === 'VWC'); // Limit to volumetric only @DG / @GC
  }, [quote, originalQuote, tenant]);

  const onAcceptPriceChange = useCallback(() => {
    onAccept();
    track({
      type: 'price_change_accepted',
      payload: {
        messages: messages.map((m) => m.type),
        originalQuotePrice: originalQuote.price.net,
        newQuotePrice: quote.price.net
      }
    });
  }, [messages, onAccept, originalQuote.price.net, quote.price.net, track]);

  const onOpenServiceSwitcher = useCallback(() => {
    setOpenSwitcher(true);
    track({
      type: 'price_change_service_switch',
      payload: {
        messages: messages.map((m) => m.type),
        originalQuotePrice: originalQuote.price.net,
        newQuotePrice: quote.price.net
      }
    });
  }, [messages, originalQuote.price.net, quote.price.net, track]);

  if (!messages || messages.length === 0) {
    return null;
  }

  const subMessages = messages.filter((m) => m.subKey);

  return (
    <>
      <P2GDialog open={!openSwitcher} onClose={() => {}} title="" dataTestId="price-change-dialog">
        <P2GGrid container direction="column" gap={2} padding={3}>
          <P2GGrid item>
            <P2GGrid container gap={2} alignItems="center">
              <P2GText variant="h3" dataTestId="price-change-dialog-title">
                {t('PRICE_CHANGE_MODAL_TITLE', { defaultValue: 'Price Update' })}
              </P2GText>
            </P2GGrid>
          </P2GGrid>
          <P2GGrid item>
            <P2GGrid container direction="column" gap={1}>
              {messages.map((message) => (
                <P2GGrid item key={message.key}>
                  <P2GText variant="body1" key={message.key}>
                    <Trans
                      t={t}
                      i18nKey={message.key}
                      defaults={message.messageText}
                      values={{
                        oldPrice: toDisplayCurrency(originalQuote.currency, originalQuote.price.net),
                        newPrice: toDisplayCurrency(quote.currency, quote.price.net),
                        courier: quote.service.courier,
                        service: quote.service.name,
                        amount: message.amount
                      }}
                      components={[<P2GText fontWeight="bold" colour="primary" />]}
                    />
                  </P2GText>
                </P2GGrid>
              ))}
            </P2GGrid>
          </P2GGrid>
          <P2GGrid item>
            <P2GText variant="body1" dataTestId="price-change-dialog-difference" fontWeight="bold" component="p">
              <Trans
                t={t}
                i18nKey={'PRICE_CHANGE_MODAL_PRICE'}
                defaults={'The price has changed from {{oldPrice}} to {{newPrice}} exc {{taxName}}.'}
                values={{
                  oldPrice: toDisplayCurrency(originalQuote.currency, originalQuote.price.net),
                  newPrice: toDisplayCurrency(quote.currency, quote.price.net),
                  courier: quote.service.courier,
                  service: quote.service.name,
                  taxName: taxName
                }}
              />
            </P2GText>
          </P2GGrid>
          <P2GGrid item>
            <P2GGrid container gap={2} dirextion="row" wrap="nowrap">
              <P2GGrid item xs={12} sm={6}>
                <P2GButton
                  fullWidth
                  size="large"
                  variant="tertiary"
                  onClick={onOpenServiceSwitcher}
                  dataTestId="price-change-dialog-switch-button"
                >
                  {t('PRICE_CHANGE_MODAL_SWITCH_SERVICE_BUTTON', { defaultValue: 'Change Service' })}
                </P2GButton>
              </P2GGrid>
              <P2GGrid item xs={12} sm={6}>
                <P2GButton
                  fullWidth
                  size="large"
                  variant="primary"
                  onClick={onAcceptPriceChange}
                  dataTestId="price-change-dialog-accept-button"
                >
                  {t('PRICE_CHANGE_MODAL_ACCEPT_BUTTON', { defaultValue: 'Accept Update' })}
                </P2GButton>
              </P2GGrid>
            </P2GGrid>
          </P2GGrid>
          {subMessages.length > 0 && (
            <P2GGrid item>
              <P2GGrid container direction="column" gap={1}>
                {subMessages.map((message) => (
                  <P2GGrid item key={message.key}>
                    <P2GText dataTestId="price-change-dialog-cancel-message">
                      <Trans
                        t={t}
                        i18nKey={message.subKey}
                        defaults={message.subMessageText || ' '}
                        values={{ courier: quote.service.courier }}
                        components={
                          message.type === 'VWC'
                            ? [
                                <P2GLink
                                  href="https://www.parcel2go.com/volume-calculator"
                                  target="_blank"
                                  dataTestId="price-change-dialog-cancel-button"
                                />
                              ]
                            : [<P2GText fontWeight="bold" colour="primary" />]
                        }
                      />
                    </P2GText>
                  </P2GGrid>
                ))}
              </P2GGrid>
            </P2GGrid>
          )}
        </P2GGrid>
      </P2GDialog>
      <Suspense>
        {openSwitcher && (
          <BasketServiceSwitcher
            open={openSwitcher}
            onClose={() => setOpenSwitcher(false)}
            onChange={onAcceptPriceChange}
          />
        )}
      </Suspense>
    </>
  );
}
